import React, { useState, useRef, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Combobox from '../ui/Combobox';
import SearchableCombobox from '../ui/SearchableCombobox';
import { FileText, Paperclip, X, CheckCircle, ArrowRight, ArrowLeft, Tag, Building2 } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import RichTextEditor from '../ui/RichTextEditor';

const steps = [
  { key: 'details', label: 'File Details' },
  { key: 'note', label: 'Initial Note' },
  { key: 'attachments', label: 'Attachments' },
  { key: 'review', label: 'Review & Create' }
];

const CreateFileWizard = ({ isOpen, onClose, onFileCreated, parentId = null }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const isSubmitting = useRef(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    category: 'General',
    initialNote: '',
    wingId: '',
    departmentId: '',
    sectionId: '',
    eSignPin: ''
  });
  
  const [files, setFiles] = useState([]);
  
  // Hierarchy State
  const [wings, setWings] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    if (isOpen) {
        fetchWings();
    }
  }, [isOpen]);

  const fetchWings = async () => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/hierarchy/wings', { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) setWings(response.data.data);
    } catch (error) {
        console.error('Failed to fetch wings', error);
    }
  };

  const fetchDepartments = async (wId) => {
    if (!wId) { setDepartments([]); return; }
    try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/hierarchy/wings/${wId}/departments`, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) setDepartments(response.data.data);
    } catch (error) {
        console.error('Failed to fetch departments', error);
    }
  };

  const fetchSections = async (dId) => {
    if (!dId) { setSections([]); return; }
    try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/hierarchy/departments/${dId}/sections`, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) setSections(response.data.data);
    } catch (error) {
        console.error('Failed to fetch sections', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleHierarchyChange = (field, val) => {
    setFormData(prev => {
        const newData = { ...prev, [field]: val };
        if (field === 'wingId') {
            newData.departmentId = '';
            newData.sectionId = '';
            fetchDepartments(val);
            setSections([]);
        } else if (field === 'departmentId') {
            newData.sectionId = '';
            fetchSections(val);
        }
        return newData;
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files)]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep = () => {
    if (currentStep === 0) {
      if (!formData.subject.trim()) {
        toast.error('Subject is required');
        return false;
      }
    }
    if (currentStep === 3) {
      if (!formData.eSignPin || String(formData.eSignPin).trim().length !== 4) {
        toast.error('Please enter your 4-digit e-Sign PIN');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    setLoading(true);
    let createdFileId = null;

    try {
      const token = localStorage.getItem('token');
      
      // 1. Create File
      const response = await axios.post(
        '/api/efile',
        { 
          subject: formData.subject,
          category: formData.category,
          initialNote: formData.initialNote,
          parentId,
          wingId: formData.wingId,
          departmentId: formData.departmentId,
          sectionId: formData.sectionId,
          eSignPin: formData.eSignPin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const newFile = response.data.data;
        createdFileId = newFile.id;
        
        // 2. Upload Attachments if any
        if (files.length > 0) {
          const uploadPromises = files.map(file => {
            const formData = new FormData();
            formData.append('files', file);
            return axios.post(
              `/api/efile/${newFile.id}/upload`,
              formData,
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
            );
          });
          
          await Promise.all(uploadPromises);
          toast.success(`File created with ${files.length} attachments`);
        } else {
          toast.success('e-File created successfully');
        }

        onFileCreated(newFile);
        onClose();
        
        // Reset state
        setCurrentStep(0);
        setFormData({ subject: '', category: 'General', initialNote: '', wingId: '', departmentId: '', sectionId: '', eSignPin: '' });
        setFiles([]);
      }
    } catch (error) {
      console.error('Error creating file:', error);
      
      // Rollback: Delete the file if it was created but upload failed
      if (createdFileId) {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`/api/efile/${createdFileId}`, {
             headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Rolled back file creation due to error');
        } catch (rollbackError) {
          console.error('Rollback failed:', rollbackError);
        }
      }

      toast.error(error.response?.data?.message || 'Failed to create file');
    } finally {
      setLoading(false);
      isSubmitting.current = false;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New e-File" size="3xl">
      <div className="flex flex-col h-full max-h-[85vh]">
        {/* Stepper Header */}
        <div className="mb-6 px-4">
          <div className="flex items-center justify-between relative">
            {/* Progress Bar Background */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-100 -z-10 rounded-full" />
            
            {/* Steps */}
            {steps.map((step, index) => {
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              
              return (
                <div key={step.key} className="flex flex-col items-center gap-2 bg-white px-2">
                  <div 
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200
                      ${isCompleted ? 'bg-green-100 text-green-600 ring-2 ring-green-500 ring-offset-2' : 
                        isCurrent ? 'bg-indigo-600 text-white ring-2 ring-indigo-600 ring-offset-2' : 
                        'bg-gray-100 text-gray-500'}
                    `}
                  >
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : index + 1}
                  </div>
                  <span className={`text-xs font-medium ${isCurrent ? 'text-indigo-600' : 'text-gray-500'}`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto px-1 py-2 min-h-[300px]">
          {currentStep === 0 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Subject <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="Enter the subject of the file..."
                  value={formData.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  className="w-full min-h-[100px]"
                  required
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  A clear and concise subject helps in easy retrieval later.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <Combobox
                  value={formData.category}
                  onChange={(val) => handleChange('category', val)}
                  options={[
                    { value: 'General', label: 'General' },
                    { value: 'Confidential', label: 'Confidential' },
                    { value: 'Urgent', label: 'Urgent' },
                    { value: 'Policy', label: 'Policy' },
                    { value: 'Project', label: 'Project' }
                  ]}
                  placeholder="Select Category"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Office Hierarchy (Optional)</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <SearchableCombobox 
                        items={wings.map(w => ({ label: w.name, value: w.id }))}
                        value={formData.wingId}
                        onChange={(val) => handleHierarchyChange('wingId', val)}
                        placeholder="Select Wing"
                    />
                    <SearchableCombobox 
                        items={departments.map(d => ({ label: d.name, value: d.id }))}
                        value={formData.departmentId}
                        onChange={(val) => handleHierarchyChange('departmentId', val)}
                        disabled={!formData.wingId}
                        placeholder="Select Department"
                    />
                    <SearchableCombobox 
                        items={sections.map(s => ({ label: s.name, value: s.id }))}
                        value={formData.sectionId}
                        onChange={(val) => handleChange('sectionId', val)}
                        disabled={!formData.departmentId}
                        placeholder="Select Section"
                    />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                    Defining the hierarchy helps in routing and approval workflows.
                </p>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Note (Optional)
                </label>
                <RichTextEditor
                  value={formData.initialNote}
                  onChange={(val) => handleChange('initialNote', val)}
                  placeholder="Add an opening note or remarks..."
                  className="min-h-[24rem]"
                />
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  Advanced editor enabled. Browser spellcheck and grammar suggestions are active.
                </p>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors bg-gray-50">
                <Paperclip className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                <p className="text-sm text-gray-600 mb-4">
                  Drag and drop files here, or click to select files
                </p>
                <label className="cursor-pointer">
                  <span className="bg-white px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Select Files
                  </span>
                  <input 
                    type="file" 
                    className="hidden" 
                    multiple 
                    onChange={handleFileChange} 
                  />
                </label>
              </div>

              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Selected Files ({files.length})</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white border rounded-md shadow-sm">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <FileText className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate">{file.name}</span>
                          <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button 
                          onClick={() => removeFile(index)}
                          className="text-gray-400 hover:text-red-500 transition-colors p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Summary</h3>
                
                <div className="space-y-4">
                  <div>
                    <span className="text-xs text-gray-500 block">Subject</span>
                    <p className="text-gray-900 font-medium">{formData.subject}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500 block">Category</span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 mt-1">
                        <Tag className="w-3 h-3 mr-1" />
                        {formData.category}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Attachments</span>
                      <span className="text-sm text-gray-900 mt-1">{files.length} files attached</span>
                    </div>
                  </div>

                  {formData.initialNote && (
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">Initial Note</span>
                      <div 
                        className="bg-white rounded border border-gray-200 max-h-96 overflow-y-auto"
                      >
                        <div
                          className="ProseMirror"
                          dangerouslySetInnerHTML={{ __html: formData.initialNote }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-500 bg-blue-50 p-3 rounded border border-blue-100 text-blue-700">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <p>File number will be generated automatically upon creation.</p>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">e-Sign PIN</label>
                  <Input
                    type="password"
                    value={formData.eSignPin}
                    onChange={(e) => setFormData(prev => ({ ...prev, eSignPin: e.target.value }))}
                    maxLength={4}
                    placeholder="Enter 4-digit PIN"
                    autoComplete="off"
                    className="text-center text-2xl tracking-widest font-mono"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-6 pt-4 border-t border-gray-100">
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={currentStep === 0 || loading}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {currentStep === steps.length - 1 ? (
            <Button onClick={handleSubmit} loading={loading} disabled={loading}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Create File
            </Button>
          ) : (
            <Button onClick={handleNext}>
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CreateFileWizard;
